import { useCallback } from 'react';
import { useTranslation } from './useTranslation';
import { useGenerateFullReportMutation } from 'store/api/apiSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { snackbar } from 'shared/helpers/snackbar';
import { useCachedSelectedProject } from './cached/useCachedSelectedProject';
import {
  startGenerating,
  stopGenerating,
} from 'store/slices/reportGenerationSlice';
import { reportApiSlice } from 'store/api/reportApiSlice';
import { useNavigate } from 'react-router-dom';
import { appRoute } from 'shared/constants/routes';

interface UseGenerateReportResult {
  isGenerating: boolean;
  handleRegenerate: (isFull: boolean) => Promise<void>;
}

// Hook to encapsulate report regeneration logic (full & partial)
export const useGenerateReport = (): UseGenerateReportResult => {
  const { selectedProject } = useCachedSelectedProject();
  const { currentLocale } = useTranslation();
  const { t } = useTranslation();
  const isGenerating = useAppSelector(
    state => state.reportGeneration.isGenerating
  );
  const [generateFullReport] = useGenerateFullReportMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleRegenerate = useCallback(async () => {
    if (!selectedProject?.domain) return;
    dispatch(startGenerating());
    try {
      await generateFullReport({
        domain: selectedProject.domain,
        lang: currentLocale,
      }).unwrap();
      setTimeout(() => {
        dispatch(
          reportApiSlice.util.invalidateTags([
            'UserInfo',
            'BusinessInfo',
            'BusinessInsights',
            'Metrics',
            'VisibilityRules',
            'VisibilityScore',
            'TargetAudience',
            'GeographicFocus',
            'BusinessAddress',
            'BusinessName',
          ])
        );
        dispatch(stopGenerating());
        navigate(appRoute('/dashboard'));
        snackbar(t('reportRegenerateStarted'));
      }, 4000);
    } catch (e: any) {
      snackbar(e?.data?.message || t('errorGeneric'));
      dispatch(stopGenerating());
    }
  }, [selectedProject, generateFullReport, dispatch, t]);

  return { isGenerating, handleRegenerate };
};
